import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import Configs from '@/Configs';
import { fetchOptions, handleResponse } from '@/common/apiCalls';
import { queriesKeys } from '@/queries/queries-keys';
import { useBasicQueryParams } from '@/queries/useBasicQueryParams';

import type { PreferredLanguage } from '@/views/settings/core/apiCalls';

export type EmployeePreferredLanguagePayload = {
  companyID: number;
  employeeID: number;
  preferredLanguage: PreferredLanguage | null;
};

export type GetEmployeePreferredLanguageQueryConfig = Omit<
  UseQueryOptions<EmployeePreferredLanguagePayload, Error, EmployeePreferredLanguagePayload>,
  'queryKey' | 'queryFn'
>;

/**
 * Loads the authenticated employee's persisted `preferredLanguage` from the latest employee row.
 */
export function useGetEmployeePreferredLanguage(
  queryConfig: GetEmployeePreferredLanguageQueryConfig = {}
): UseQueryResult<EmployeePreferredLanguagePayload, Error> {
  const { companyID, employeeID, token, correlationId = null, basicEnabled } =
    useBasicQueryParams();
  const { enabled: queryEnabled = true, ...restQueryConfig } = queryConfig;

  return useQuery({
    ...restQueryConfig,
    enabled: basicEnabled && queryEnabled,
    queryKey: queriesKeys.getEmployeePreferredLanguage(companyID, employeeID),
    queryFn: async () => {
      const response = await fetch(
        `${Configs.platformGetEmployeePreferredLanguageAPI}?companyID=${companyID}&employeeID=${employeeID}`,
        fetchOptions('GET', null, token, 'json', correlationId)
      );

      return handleResponse(response);
    },
  });
}
